import { useCallback, useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { DollarSign, RefreshCw, Download } from "lucide-react";
import { toast } from "sonner";
import { adminClient } from "./api";

const money = (v) => `$${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const CATS = [
  { key: "jobs", label: "Jobs", color: "#0C2E2B", note: "deposits + invoices/payments" },
  { key: "merch", label: "Merch", color: "#FFDF00", note: "paid orders" },
  { key: "used_goods", label: "Used Goods", color: "#2E8B57", note: "paid item requests" },
];

function TotalTile({ cat, data }) {
  return (
    <div data-testid={`income-total-${cat.key}`} className="rounded-md border-2 border-zinc-200 p-4 flex flex-col gap-1" style={{ borderTopColor: cat.color, borderTopWidth: 4 }}>
      <div className="text-[10px] font-tactical uppercase tracking-widest text-zinc-500">{cat.label}</div>
      <div className="font-display font-black text-2xl text-[#0C2E2B]">{money(data.totals[cat.key])}</div>
      <div className="text-[11px] text-zinc-600">This month <b>{money(data.this_month?.[cat.key])}</b> · {data.counts[cat.key]} paid</div>
      <div className="text-[10px] text-zinc-400">{cat.note}</div>
    </div>
  );
}

export default function IncomeCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const exportCsv = async (year) => {
    setExporting(true);
    try {
      const { data } = await adminClient.get(`/admin/income/export${year ? `?year=${year}` : ""}`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url; a.download = `harveys-place-income-${year || "all"}.csv`; a.click();
      URL.revokeObjectURL(url);
      toast.success(`Income CSV downloaded${year ? ` · ${year}` : ""}`);
    } catch { toast.error("Export failed"); }
    finally { setExporting(false); }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await adminClient.get("/admin/income?months=12"); setData(data); }
    catch { setData(null); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <Card data-testid="income-card" className="p-5 border-2 border-zinc-200">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <DollarSign className="w-4 h-4 text-[#0C2E2B]" />
          <h3 className="font-display font-black text-[#0C2E2B]">Income · money received</h3>
        </div>
        <div className="flex items-center gap-3">
          {data && <span className="text-[11px] text-zinc-500">Year to date <b data-testid="income-ytd" className="text-[#0C2E2B]">{money(data.ytd)}</b> · All time <b data-testid="income-all-time" className="text-[#0C2E2B]">{money(data.totals.all)}</b></span>}
          <button data-testid="income-export-year-btn" onClick={() => exportCsv(new Date().getFullYear())} disabled={exporting} className="inline-flex items-center gap-1 text-[11px] font-bold px-2.5 h-7 rounded-md bg-[#0C2E2B] text-[#FFDF00] hover:bg-[#0C2E2B]/90 disabled:opacity-50"><Download className="w-3 h-3" /> {new Date().getFullYear()} CSV</button>
          <button data-testid="income-export-all-btn" onClick={() => exportCsv(null)} disabled={exporting} className="inline-flex items-center gap-1 text-[11px] font-bold px-2.5 h-7 rounded-md border border-[#0C2E2B] text-[#0C2E2B] hover:bg-[#FFDF00]/20 disabled:opacity-50"><Download className="w-3 h-3" /> All time</button>
          <button data-testid="income-refresh-btn" onClick={load} className="text-zinc-400 hover:text-[#0C2E2B]"><RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /></button>
        </div>
      </div>
      {loading && !data ? <div className="text-xs text-zinc-500 mt-3">Adding it up…</div> : !data ? (
        <div className="text-xs text-red-600 mt-3">Could not load income</div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-4">{CATS.map((c) => <TotalTile key={c.key} cat={c} data={data} />)}</div>
          <div className="mt-5 h-56" data-testid="income-chart">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.months} margin={{ top: 4, right: 4, left: -16, bottom: 0 }}>
                <XAxis dataKey="label" tick={{ fontSize: 10 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 10 }} axisLine={false} tickLine={false} tickFormatter={(v) => `$${v}`} />
                <Tooltip formatter={(v, n) => [money(v), CATS.find((c) => c.key === n)?.label || n]} labelStyle={{ fontWeight: 700 }} contentStyle={{ fontSize: 12, borderRadius: 6, border: "2px solid #0C2E2B" }} />
                <Legend formatter={(v) => CATS.find((c) => c.key === v)?.label || v} wrapperStyle={{ fontSize: 11 }} />
                {CATS.map((c) => <Bar key={c.key} dataKey={c.key} stackId="a" fill={c.color} radius={c.key === "used_goods" ? [3, 3, 0, 0] : 0} />)}
              </BarChart>
            </ResponsiveContainer>
          </div>
          <p className="text-[10px] text-zinc-500 mt-2">Last 12 months, {data.tz.replace("America/", "")} time. Refunded and cancelled records are excluded; Zelle payments count from the day you mark them paid.</p>
        </>
      )}
    </Card>
  );
}
